// Fuzzball Quiz Trail Box Builder
// Custom element for managing trial box selections

class TrailBoxBuilder extends HTMLElement {
  constructor() {
    super();
    // Multi-cat selections storage
    this.selections = {
      cat1: [],
      cat2: [],
      cat3: [],
      cat4: []
    };
    this.currentCat = null; // Track which cat is being edited
    this.trailBoxProducts = {
      cans: [],
      dry: []
    };
    // Multi-pack size (cans per carton) — driven by the section setting
    // `trail_box_multipack_size`, parsed in init() from the data attribute on
    // the <trail-box> element. Fallback of 7 matches today's live behaviour
    // and the schema default. goalRules, twoMultipackTotal and every
    // downstream goal-validation tuple are derived from this size in
    // parseMultipackSize() once the data attribute has been read.
    this.multipackSize = 7;
    this.twoMultipackTotal = 14;
    this.goalRules = {
      option1: { cans: 14, dry: 0 },
      option2: { cans: 7, dry: 1 },
      option3: { cans: 0, dry: 2 }
    };
    this.currentGoal = 'option1'; // Default to the cans-only goal (2 × multipack)
    this.isGoalMet = false;

    // Multi-cat inventory tracker
    this.globalInventoryUsage = {}; // Track total usage across all cats
  }

  connectedCallback() {
    this.init();
  }

  /**
   * Parse the section's `trail_box_multipack_size` setting off the
   * <trail-box> element's data attribute. Falls back to 7 if the attribute
   * is missing or unparseable so legacy/edge renders keep today's behaviour.
   * Exposes:
   *   this.multipackSize       — cans per multi-pack carton (e.g. 7 or 6)
   *   this.twoMultipackTotal   — total for the cans-only goal (multipackSize × 2)
   *   this.goalRules           — the three valid goal tuples, rebuilt from
   *                              the live size so every downstream consumer
   *                              (canAddQuantity, updateGoalProgress,
   *                              restoreEditModeProducts, handleReturnToCart,
   *                              determineBestGoalOption, closeTrailBox)
   *                              can derive its 14/7/2 thresholds from one place.
   */
  parseMultipackSize() {
    const raw = this.getAttribute('data-trail-box-multipack-size');
    const parsed = parseInt(raw, 10);
    if (Number.isFinite(parsed) && parsed >= 1 && parsed <= 14) {
      this.multipackSize = parsed;
    } else {
      this.multipackSize = 7;
    }
    this.twoMultipackTotal = this.multipackSize * 2;
    this.goalRules = {
      option1: { cans: this.twoMultipackTotal, dry: 0 },
      option2: { cans: this.multipackSize, dry: 1 },
      option3: { cans: 0, dry: 2 }
    };
  }

  init() {
    // Parse the multi-pack size off the data attribute FIRST so any
    // downstream init step that derives goal rules / totals from it
    // (phase 2) reads the correct value.
    this.parseMultipackSize();

    // Load saved selections from localStorage
    const hasLoadedSelections = this.loadSelections();

    this.loadProductsFromSettings();

    // Trial-box flavour toggles (Aug 2026): saved selections may reference
    // flavours since switched off in theme settings. Reconcile every cat's
    // data before any UI restore so boxes rebuild from flavours that still
    // render (edit mode rebuilds from cart instead and reconciles in
    // restoreEditModeProducts).
    if (hasLoadedSelections) {
      this.reconcileSelectionsWithPool();
    }
    this.setupEventListeners();
    this.populateProductsUI();
    
    // Set initial price from TRIAL_BOX_PRICE
    const priceElement = document.querySelector('.trail-box-price-value');
    if (priceElement) {
      priceElement.textContent = '£' + window.QuizConstants.FOOD.TRIAL_BOX_PRICE.toFixed(2);
    } 
    
    // Check for edit mode
    const editMode = window.QuizState.get('editMode');
    console.log('📦 Trail Box init - Edit mode state:', editMode);
    if (editMode?.active) {
      // Set up return to cart button if in restricted edit mode
      if (editMode.restricted) {
        console.log('📦 Trail Box init - Restricted edit mode detected, setting up return button');
        // Delay to ensure DOM is ready
        setTimeout(() => this.setupReturnToCartButton(), 100);
      } else {
        console.log('📦 Trail Box init - Edit mode active but not restricted');
      }
      
      // Always check if we have trail products to restore, regardless of editType
      // This allows switching between tabs in edit mode
      if (editMode.products?.trail) {
        this.restoreEditModeProducts(editMode);
      }
    } else if (hasLoadedSelections) {
      // If we loaded saved selections and not in edit mode, restore the UI
      this.restoreUIFromSelections();
    }
    
    // Calculate initial global inventory usage
    this.calculateGlobalInventoryUsage();
    
    // Check inventory status on init
    this.updateInputStates();
    
    // Update the summary to ensure trail box counts are correct
    this.updateSummary();
  }
  
  restoreEditModeProducts(editMode) {
    if (!editMode.products?.trail) {
      return;
    }
    
    console.log('📦 Restoring trail products from edit mode:', editMode.products.trail);
    
    // In edit mode, we should only track what's actually in the cart
    // Clear ALL selections first, then rebuild from cart
    this.selections = {
      cat1: [],
      cat2: [],
      cat3: [],
      cat4: []
    };
    
    // Recalculate global inventory usage after clearing all
    this.calculateGlobalInventoryUsage();
    
    // Clear all quantity inputs first
    const allQuantityInputs = this.querySelectorAll('.quantity-input');
    allQuantityInputs.forEach(input => {
      input.value = 0;
    });
    
    // Count totals as we restore
    let totalCans = 0;
    let totalDry = 0;
    
    // Restore each product FROM CART (these are what user actually selected)
    editMode.products.trail.forEach(savedProduct => {
      console.log('📦 Trying to restore product:', savedProduct);
      
      // Try multiple ways to find the product card
      let productCard = this.querySelector(`[data-product-id="${savedProduct.id}"]`);
      if (!productCard && savedProduct.variant_id) {
        productCard = this.querySelector(`[data-product-id="${savedProduct.variant_id}"]`);
      }
      // Also try with the product handle if available
      if (!productCard && savedProduct.handle) {
        productCard = Array.from(this.querySelectorAll('[data-product-id]')).find(card => {
          const title = card.querySelector('.trail-box-card-title')?.textContent;
          return title && title.toLowerCase().includes(savedProduct.handle.toLowerCase());
        });
      }
      // Try finding by title
      if (!productCard && savedProduct.title) {
        productCard = Array.from(this.querySelectorAll('[data-product-id]')).find(card => {
          const title = card.querySelector('.trail-box-card-title')?.textContent;
          return title && title.trim() === savedProduct.title.trim();
        });
      }
      
      if (productCard) {
        const quantityInput = productCard.querySelector('.quantity-input');
        if (quantityInput) {
          quantityInput.value = savedProduct.quantity;
          // Update selection using the data-product-id from the card
          const productId = productCard.dataset.productId;
          const productType = savedProduct.type || productCard.dataset.productType;
          console.log('📦 Found product card. Type:', productType, 'Quantity:', savedProduct.quantity);
          this.updateSelection(productId, productType, savedProduct.quantity);
          
          // Track totals
          if (productType === 'can') {
            totalCans += savedProduct.quantity;
          } else if (productType === 'dry') {
            totalDry += savedProduct.quantity;
          }
        }
      } else {
        console.error('📦 Could not find product card for:', savedProduct);
        console.log('📦 Available product cards:', this.querySelectorAll('[data-product-id]'));
      }
    });
    
    console.log('📦 Restored totals - Cans:', totalCans, 'Dry:', totalDry);

    // Trial-box flavour toggles (Aug 2026): cart lines whose flavour card no
    // longer renders (switched off in theme settings) are skipped above, so
    // the box can come back short. Top cans back up to the goal implied by
    // the dry count, drawing only from flavours still on sale.
    const impliedCanTarget = totalDry >= 2 ? 0 : (totalDry === 1 ? this.multipackSize : this.twoMultipackTotal);
    if (totalCans > 0 || totalDry > 0) {
      const canDeficit = impliedCanTarget - totalCans;
      if (canDeficit > 0) {
        const dislikes = this.getCatDislikes(this.currentCat || 'cat1');
        const pool = this.getFilteredProductsWithFallback('can', dislikes).cards;
        if (pool.length > 0) {
          console.log(`📦 Topping up ${canDeficit} can(s) from available flavours`);
          this.autoSelectProducts(pool, 'can', canDeficit);
          totalCans = 0;
          const catSelections = this.selections[this.currentCat || 'cat1'] || [];
          catSelections.forEach(sel => {
            if (sel.type === 'can') totalCans += sel.quantity;
          });
          if (window.QuizUtils && window.QuizUtils.Error) {
            window.QuizUtils.Error.show('Some flavours are temporarily unavailable, so we\'ve topped up your box with our other flavours');
          }
        }
      }
    }

    // Check if the restored quantities match valid rules. Tuples derive from
    // the live multi-pack size so pre-flip carts (14 / 7+1) are correctly
    // flagged as invalid after a supplier-swap flip to 6 (12 / 6+1).
    const isValidCombination = (totalCans === this.twoMultipackTotal && totalDry === 0) ||
                               (totalCans === this.multipackSize && totalDry === 1) ||
                               (totalCans === 0 && totalDry === 2);

    if (!isValidCombination) {
      console.warn('📦 WARNING: Restored products do not match valid trail box rules!');
      console.warn(`📦 Valid combinations: ${this.twoMultipackTotal} cans, ${this.multipackSize} cans + 1 dry, or 2 dry bags`);

      // Auto-adjust to nearest valid combination if needed.
      // This helps when cart has been modified outside the quiz, or — the case
      // this card is built for — when a pre-flip cart (14 cans / 7+1) is
      // restored after the operator has flipped the multi-pack size to 6.
      // Surface a user-facing toast so the customer is told the goal shifted
      // and prompted to confirm a valid combination before returning to cart.
      const adjustMessage = 'Trail box adjusted based on available inventory';
      if (totalCans > 0 && totalDry === 0) {
        // Adjust to cans-only goal (multipackSize × 2)
        console.log(`📦 Auto-adjusting to ${this.twoMultipackTotal} cans goal...`);
        if (window.QuizUtils && window.QuizUtils.Error) {
          window.QuizUtils.Error.show(adjustMessage);
        }
      } else if (totalCans > 0 && totalDry > 0) {
        // Adjust to mixed goal (multipackSize cans + 1 dry)
        console.log(`📦 Auto-adjusting to ${this.multipackSize} cans + 1 dry goal...`);
        if (window.QuizUtils && window.QuizUtils.Error) {
          window.QuizUtils.Error.show(adjustMessage);
        }
      } else if (totalDry > 0) {
        // Adjust to 2 dry goal
        console.log('📦 Auto-adjusting to 2 dry bags goal...');
        if (window.QuizUtils && window.QuizUtils.Error) {
          window.QuizUtils.Error.show(adjustMessage);
        }
      }
    }
    
    // Update the summary and UI
    this.updateSummary();
    this.updateGoalProgress();
    this.updateInputStates();
    
    // Important: Do NOT auto-populate from quiz after this
  }

  loadProductsFromSettings() {
    // Get products directly from data attributes (from schema settings)
    const cansData = this.getAttribute('data-trail-box-cans');
    const dryData = this.getAttribute('data-trail-box-dry-foods');
    
    try {
      this.trailBoxProducts.cans = cansData ? JSON.parse(cansData) : [];
      this.trailBoxProducts.dry = dryData ? JSON.parse(dryData) : [];
      
    } catch (e) {
    }
  }

  setupEventListeners() {
    // Quantity control listeners
    this.addEventListener('click', (e) => {
      if (e.target.closest('.quantity-btn')) {
        const button = e.target.closest('.quantity-btn');
        const change = parseInt(button.dataset.change);
        this.handleQuantityChange(button, change);
      }
    });

    // Check if in edit mode and set up return to cart button
    const editMode = window.QuizState.get('editMode');
    if (editMode?.active && editMode?.restricted) {
      this.setupReturnToCartButton();
    } else {
      // Normal close button listener
      const closeBtn = document.querySelector('.trail-box-system-close');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.closeTrailBox());
      }
    }
  }
  
  setupReturnToCartButton() {
    console.log('📦 Setting up return to cart button for trail box');
    
    // Check edit mode state
    const editMode = window.QuizState.get('editMode');
    console.log('📦 Edit mode state in setupReturnToCartButton:', editMode);
    
    if (!editMode?.restricted) {
      console.log('📦 Not in restricted edit mode, skipping button setup');
      return;
    }
    
    // Check if button already exists
    if (document.querySelector('.trail-box-return-to-cart')) {
      console.log('Button already exists');
      return;
    }
    
    // Wait for trail box system to be active
    const trailBoxSystem = document.querySelector('.trail-box-system');
    if (!trailBoxSystem || !trailBoxSystem.classList.contains('trail-box-system-active')) {
      console.log('📦 Trail box not active yet, retrying...');
      setTimeout(() => this.setupReturnToCartButton(), 100);
      return;
    }
    
    // Create return to cart button
    const returnBtn = document.createElement('button');
    returnBtn.className = 'trail-box-return-to-cart button button--primary';
    returnBtn.innerHTML = 'Return to Cart';
    
    returnBtn.addEventListener('click', () => this.handleReturnToCart());
    
    // Try multiple locations for the button
    // First try the header
    const trailBoxHeader = document.querySelector('.trail-box-system-header');
    if (trailBoxHeader) {
      console.log('Adding button to header');
      // Hide the close button
      const closeBtn = trailBoxHeader.querySelector('.trail-box-system-close');
      if (closeBtn) {
        closeBtn.style.display = 'none';
      }
      
      // Add return button to header
      trailBoxHeader.appendChild(returnBtn);
      console.log('📦 Return to cart button successfully added to header');
    } else {
      // Fallback: try the footer or actions area
      const trailBoxFooter = document.querySelector('.trail-box-system-footer, .trail-box-actions');
      if (trailBoxFooter) {
        console.log('Adding button to footer/actions');
        trailBoxFooter.appendChild(returnBtn);
        console.log('📦 Return to cart button successfully added to footer');
      } else {
        console.log('📦 ERROR: Could not find suitable location for return button');
        console.log('📦 Available elements:', {
          header: document.querySelector('.trail-box-system-header'),
          footer: document.querySelector('.trail-box-system-footer'),
          actions: document.querySelector('.trail-box-actions'),
          system: document.querySelector('.trail-box-system')
        });
      }
    }
  }
  
  async handleReturnToCart() {
    console.log('📦 handleReturnToCart called');
    
    // Prevent double-clicks
    const returnBtn = document.querySelector('.trail-box-return-to-cart');
    if (returnBtn && returnBtn.disabled) {
      console.log('📦 Return button already processing, ignoring duplicate click');
      return;
    }
    
    // Validate trail box rules
    const cansCount = this.getTotalQuantityByType('can');
    const dryCount = this.getTotalQuantityByType('dry');
    
    console.log('📦 Current selection - Cans:', cansCount, 'Dry:', dryCount);
    
    const isValid = (cansCount === this.twoMultipackTotal && dryCount === 0) ||
                   (cansCount === this.multipackSize && dryCount === 1) ||
                   (cansCount === 0 && dryCount === 2);

    if (!isValid) {
      console.error('📦 Invalid trail box combination!');
      console.error(`📦 Valid combinations: ${this.twoMultipackTotal} cans, ${this.multipackSize} cans + 1 dry, or 2 dry bags`);
      window.QuizUtils.Error.show(`Please select a valid combination: ${this.twoMultipackTotal} cans, ${this.multipackSize} cans + 1 dry, or 2 dry foods`);
      return;
    }
    
    console.log('📦 Trail box validation passed');
    
    // Get edit mode info
    const editMode = window.QuizState.get('editMode');
    if (!editMode || !editMode.quizId) {
      console.error('📦 No edit mode info found');
      return;
    }
    
    try {
      // Show loading state and disable button immediately
      if (returnBtn) {
        returnBtn.disabled = true;
        returnBtn.textContent = 'Updating cart...';
      }
      
      // Get current selections - only for the cat being edited in restricted mode
      let trailItems;
      const isMultiCat = window.QuizState.get('submissionData.catCount') > 1;
      
      if (editMode.restricted && isMultiCat) {
        // In restricted edit mode, only get selections for the specific cat
        this.editingCat = editMode.catNumber;
        trailItems = this.getSelections(false); // Get only current cat
      } else {
        // Normal mode or single cat - get all selections
        trailItems = this.getSelections(true); // Get all cats
      }
      
      
      // Remove old trail AND extras items for this quiz and cat
      await window.QuizCartAPI.removeCartItemsByQuizId(editMode.quizId, 'trail', editMode.catNumber);
      await window.QuizCartAPI.removeCartItemsByQuizId(editMode.quizId, 'extras', editMode.catNumber);

      // Get current extras selections from QuizExtrasManager — keyed by cat so buildQuizCartItems
      // uses the per-cat object path (not the legacy array path which hardcodes cat 1)
      const catKey = `cat${editMode.catNumber}`;
      const rawExtras = window.QuizExtrasManager ? window.QuizExtrasManager.getExtras(editMode.catNumber) : [];
      const extrasItems = { [catKey]: rawExtras };

      // Get submission data and quiz data
      const submissionData = window.QuizState.get('submissionData') || {};
      const catName = submissionData[`cat${editMode.catNumber}`]?.fuzzballer_name || `Cat ${editMode.catNumber}`;

      // Build quiz cart items — trail + extras together
      const cartItems = await window.QuizCartAPI.buildQuizCartItems(
        submissionData,  // submission data
        [],              // bundle items (empty for trail edit)
        trailItems,      // trail items
        extrasItems,     // extras items keyed by cat
        true,            // isEditMode
        editMode.quizId  // editQuizId
      );
      
      // Add new items to cart
      await window.QuizCartAPI.addItems(cartItems);
      
      // Clear edit mode data
      localStorage.removeItem(`edit_${editMode.quizId}_data`);
      localStorage.removeItem(`edit_${editMode.quizId}_products`);
      
      // Redirect to cart
      window.location.href = '/cart';
      
    } catch (error) {
      console.error('📦 Error updating cart:', error);
      window.QuizUtils.Error.show('Error updating cart. Please try again.');
      
      // Re-enable button
      const returnBtn = document.querySelector('.trail-box-return-to-cart');
      if (returnBtn) {
        returnBtn.disabled = false;
        returnBtn.textContent = 'Return to Cart';
      }
    }
  }

  populateProductsUI() {
    // Products are already in the DOM from Liquid template
    // Just need to ensure they have the right data attributes
    const productCards = this.querySelectorAll('.product-card');
    productCards.forEach(card => {
      const productId = card.dataset.productId;
      const productType = card.dataset.productType;
      
      // Store product info for easy access
      const titleElement = card.querySelector('.product-title');
      if (titleElement && productId) {
        const productInfo = {
          id: productId,
          title: titleElement.textContent,
          type: productType
        };
        
        // Store in our product list for quick access
        this.trailBoxProducts[productType === 'can' ? 'cans' : 'dry'].forEach(product => {
          if (product.id.toString() === productId) {
            Object.assign(productInfo, product);
          }
        });
      }
    });
  }

  handleQuantityChange(button, change) {
    const productCard = button.closest('.product-card');
    const quantityInput = productCard.querySelector('.quantity-input');
    const productId = productCard.dataset.productId;
    const productType = productCard.dataset.productType;
    const productTitle = productCard.querySelector('.product-title')?.textContent || 'Product';

    // User-driven edit on the trail box — mark this cat's trail as user-edited
    // so subsequent step-7 re-entries preserve their picks instead of wiping
    // them via autoPopulateFromQuiz's clearAllSelections() call.
    if (window.QuizState && typeof window.QuizState.markSelectionUserEdited === 'function') {
      window.QuizState.markSelectionUserEdited('trail', this.currentCat || `cat${this.editingCat || 1}`);
    }

    let newQuantity = parseInt(quantityInput.value) + change;
    if (newQuantity < 0) newQuantity = 0;
    
    // Check inventory limits with multi-cat awareness
    if (change > 0) {
      const availableInventory = this.getAvailableInventory(productId, productCard);
      const currentCatUsage = this.getCurrentCatUsage(productId);
      const maxAllowedForCat = currentCatUsage + availableInventory;
      
      if (newQuantity > maxAllowedForCat) {
        // Show toast about inventory limit
        if (window.QuizUtils && window.QuizUtils.Error) {
          if (availableInventory === 0) {
            window.QuizUtils.Error.show(`${productTitle} - No more available (used by other cats)`);
          } else {
            window.QuizUtils.Error.show(`${productTitle} - Only ${availableInventory} more available`);
          }
        }
        return; // Don't proceed with the change
      } else if (availableInventory === 1 && change === 1) {
        // Show toast when taking the last available item
        if (window.QuizUtils && window.QuizUtils.Error) {
          window.QuizUtils.Error.show(`${productTitle} - Last one available!`);
        }
      }
    }
    
    // Check if this change would violate goal rules
    if (!this.canAddQuantity(productType, change)) {
      return;
    }
    
    // Update input value
    quantityInput.value = newQuantity;
    
    // Show Lottie animation when adding items
    if (change > 0 && newQuantity > 0) {
      this.showQuantityAnimation(productCard);
    }
    
    // Update selection
    this.updateSelection(productId, productType, newQuantity);
    
    // Update all UI components
    this.updateSummary();
    this.updateGoalProgress(); 
    this.updateInputStates();
    
  }

  canAddQuantity(productType, change) {
    const currentCans = this.getTotalQuantityByType('can');
    const currentDry = this.getTotalQuantityByType('dry');
    
    // Calculate what the new totals would be
    let newCans = currentCans;
    let newDry = currentDry;
    
    if (productType === 'can') {
      newCans += change;
    } else if (productType === 'dry') {
      newDry += change;
    }
    
    // Check if the new state would match any valid goal. Tuples come from
    // goalRules (rebuilt from the live multi-pack size in parseMultipackSize).
    const validGoals = [
      { cans: this.goalRules.option1.cans, dry: this.goalRules.option1.dry },
      { cans: this.goalRules.option2.cans, dry: this.goalRules.option2.dry },
      { cans: this.goalRules.option3.cans, dry: this.goalRules.option3.dry }
    ];
    
    // Allow if moving toward any valid goal
    for (const goal of validGoals) {
      // Check if we're at or moving toward this goal
      if ((newCans <= goal.cans && newDry <= goal.dry) || 
          (newCans === goal.cans && newDry === goal.dry)) {
        return true;
      }
    }
    
    return false;
  }

  updateSelection(productId, productType, quantity) {
    // Get current cat key
    const catKey = this.currentCat || 'cat1';
    
    // Ensure cat array exists
    if (!this.selections[catKey]) {
      this.selections[catKey] = [];
    }
    
    const catSelections = this.selections[catKey];
    const existingIndex = catSelections.findIndex(s => s.id === productId);
    
    if (quantity === 0) {
      if (existingIndex !== -1) {
        catSelections.splice(existingIndex, 1);
      }
    } else {
      // Get additional product info from the product card
      const productCard = document.querySelector(`[data-product-id="${productId}"]`);
      let variantId = productCard?.dataset.variantId;
      let title = productCard?.querySelector('.product-title')?.textContent;
      
      // Fallback: get variant ID from loaded product data if DOM doesn't have it
      if (!variantId) {
        const productData = this.findProductById(productId);
        if (productData) {
          variantId = productData.variant_id;
          title = productData.title;
        }
      }
      
      
      // Get the cat number being edited - use this.editingCat if available
      const catNumber = this.editingCat || catKey.replace('cat', '') || '1';
      
      const selectionData = {
        id: productId,
        variant_id: variantId,  // Changed from variantId to variant_id to match cart API
        title: title,
        type: productType,
        quantity: quantity,
        cat_number: catNumber // Add cat number to selection
      };
      
      if (existingIndex !== -1) {
        catSelections[existingIndex] = selectionData;
      } else {
        catSelections.push(selectionData);
      }
    }
    
    // Recalculate global inventory usage whenever selections change
    this.calculateGlobalInventoryUsage();
    
    // Save selections to localStorage after each update
    this.saveSelections();
  }

  findProductById(productId) {
    // Search in both cans and dry arrays
    for (const product of [...this.trailBoxProducts.cans, ...this.trailBoxProducts.dry]) {
      if (product.id.toString() === productId.toString()) {
        return product;
      }
    }
    return null;
  }

  // Calculate total inventory usage across all cats
  calculateGlobalInventoryUsage() {
    // Reset global usage
    this.globalInventoryUsage = {};
    
    // Sum up usage from all cats
    Object.entries(this.selections).forEach(([catKey, catSelections]) => {
      if (Array.isArray(catSelections)) {
        catSelections.forEach(selection => {
          const productId = selection.id;
          if (!this.globalInventoryUsage[productId]) {
            this.globalInventoryUsage[productId] = 0;
          }
          this.globalInventoryUsage[productId] += selection.quantity;
        });
      }
    });
    
  }

  // Get available inventory for a product considering all cats' usage
  getAvailableInventory(productId, productCard) {
    const inventoryQty = parseInt(productCard?.dataset.inventoryQuantity || '0');
    const inventoryPolicy = productCard?.dataset.inventoryPolicy || 'deny';
    
    // If inventory tracking is disabled, return high number
    if (inventoryPolicy === 'continue') {
      return 999;
    }
    
    // Get global usage for this product
    const globalUsage = this.globalInventoryUsage[productId] || 0;
    
    // Available = total inventory - global usage across all cats
    const available = Math.max(0, inventoryQty - globalUsage);
    
    return available;
  }

  // Get current cat's usage for a product
  getCurrentCatUsage(productId) {
    const catKey = this.currentCat || 'cat1';
    const catSelections = this.selections[catKey] || [];
    const selection = catSelections.find(s => s.id === productId);
    return selection ? selection.quantity : 0;
  }

  updateSummary() {
    // Get current cat's selections
    const catKey = this.currentCat || 'cat1';
    const catSelections = this.selections[catKey] || [];
    const totalItems = catSelections.reduce((total, selection) => total + selection.quantity, 0);
    
    // Dispatch event for unified products manager
    const catNumber = parseInt(catKey.replace('cat', ''));
    document.dispatchEvent(new CustomEvent('trail-box-updated', {
      detail: {
        catNumber: catNumber,
        selections: catSelections
      }
    }));
    
    // Get the cat number being edited from the trail box system
    const trailBoxSystem = document.querySelector('.trail-box-system');
    const editingCat = trailBoxSystem?.getAttribute('data-editing-cat');
    
    if (editingCat) {
      // Update only the specific cat's trail box item count
      const catSection = document.querySelector(`.trail-box-section[data-cat="${editingCat}"]`);
      if (catSection) {
        const itemsCountElement = catSection.querySelector('.trail-box-items-count');
        if (itemsCountElement) {
          itemsCountElement.textContent = totalItems;
        }
      }
    } else {
      // Fallback: Update all trail box item count displays (for single cat mode)
      const itemsCountElements = document.querySelectorAll('.trail-box-items-count');
      itemsCountElements.forEach(element => {
        element.textContent = totalItems;
      });
    }
    
    // Update trail box price from TRIAL_BOX_PRICE
    const priceElement = document.querySelector('.trail-box-price-value');
    if (priceElement) {
      priceElement.textContent = '£' + window.QuizConstants.FOOD.TRIAL_BOX_PRICE.toFixed(2);
    }
  }

  updateGoalProgress() {
    const currentCans = this.getTotalQuantityByType('can');
    const currentDry = this.getTotalQuantityByType('dry');

    // Cache the live multi-pack derived totals for use in helper-text
    // template literals below. multipackSize is what one multi-pack carton
    // holds (7 today, 6 after the supplier swap); twoMultipackTotal is the
    // cans-only goal (multipackSize × 2 → 14 today, 12 after the swap).
    const multipackSize = this.multipackSize;
    const twoMultipackTotal = this.twoMultipackTotal;

    // Define all valid goal combinations. Tuples are derived from the live
    // multi-pack size so every number on the customer-visible banner scales
    // with the customizer setting.
    const validGoals = [
      { cans: twoMultipackTotal, dry: 0, name: `${twoMultipackTotal} cans` },
      { cans: multipackSize, dry: 1, name: `${multipackSize} cans + 1 dry` },
      { cans: 0, dry: 2, name: '2 dry bags' }
    ];

    // Check if current state matches any complete goal EXACTLY
    const completedGoal = validGoals.find(goal =>
      currentCans === goal.cans && currentDry === goal.dry
    );

    if (completedGoal) {
      const goalText = `Goal achieved: ${completedGoal.name}`;
      const remainingText = '🎉 Trail box complete!';

      this.updateGoalDisplay(goalText, remainingText);
      this.isGoalMet = true;
      return;
    }

    // Not complete - find the most logical goal based on current selection
    let closestGoal = null;

    // If user has started with cans, prioritize can-based goals
    if (currentCans > 0 && currentDry === 0) {
      // User has cans, suggest can-based goals
      if (currentCans < multipackSize) {
        closestGoal = validGoals.find(g => g.cans === multipackSize && g.dry === 1); // multipack + 1 dry
      } else if (currentCans < twoMultipackTotal) {
        closestGoal = validGoals.find(g => g.cans === twoMultipackTotal && g.dry === 0); // 2 × multipack cans-only
      }
    }
    // If user has started with dry, prioritize dry-based goals
    else if (currentDry > 0 && currentCans === 0) {
      closestGoal = validGoals.find(g => g.cans === 0 && g.dry === 2); // 2 dry goal
    }
    // If user has both, find closest mixed goal
    else if (currentCans > 0 && currentDry > 0) {
      closestGoal = validGoals.find(g => g.cans === multipackSize && g.dry === 1); // multipack + 1 dry
    }

    // Fallback: find the closest goal by distance
    if (!closestGoal) {
      let minDistance = Infinity;
      validGoals.forEach(goal => {
        const cansNeeded = Math.max(0, goal.cans - currentCans);
        const dryNeeded = Math.max(0, goal.dry - currentDry);
        const distance = cansNeeded + dryNeeded;

        if (distance < minDistance && distance > 0) {
          minDistance = distance;
          closestGoal = goal;
        }
      });
    }

    if (closestGoal) {
      const cansNeeded = Math.max(0, closestGoal.cans - currentCans);
      const dryNeeded = Math.max(0, closestGoal.dry - currentDry);

      // Special handling for very low inventory situations. Threshold is
      // multipackSize — once a customer has at least one full carton's
      // worth of cans, the "regular" messages below handle them.
      if ((currentCans + currentDry) < multipackSize) {
        let remainingText = '';
        if (currentCans === 1 && currentDry === 0) {
          // Special message for 1 can only — to reach multipack + 1 dry they
          // need (multipackSize - 1) more cans, OR they can switch to a dry path.
          const cansToMixedGoal = multipackSize - 1;
          remainingText = `Add ${cansToMixedGoal} more can${cansToMixedGoal === 1 ? '' : 's'} OR add 1 dry bag to reach a valid goal`;
        } else if (currentDry === 1 && currentCans === 0) {
          // Special message for 1 dry only — add 1 more dry to hit 2 dry,
          // OR add a full multipack worth of cans to hit the mixed goal.
          remainingText = `Add 1 more dry bag OR add ${multipackSize} cans to reach a valid goal`;
        } else {
          // General message for other low inventory cases
          const parts = [];
          if (cansNeeded > 0) parts.push(`${cansNeeded} more can${cansNeeded > 1 ? 's' : ''}`);
          if (dryNeeded > 0) parts.push(`${dryNeeded} more dry bag${dryNeeded > 1 ? 's' : ''}`);
          remainingText = 'Add ' + parts.join(' and ');
        }
        this.updateGoalDisplay(`Goal: ${closestGoal.name}`, remainingText);
      } else {
        // Normal message for regular cases
        let remainingText = '';

        // Special case: if user has exactly multipackSize cans, show both options
        // (continue to 2 × multipack cans-only, or branch to multipack + 1 dry).
        if (currentCans === multipackSize && currentDry === 0) {
          remainingText = `Add ${multipackSize} more cans OR add 1 dry bag`;
        }
        // Special case: if user has between multipackSize+1 and twoMultipackTotal-1 cans,
        // they're on track for the cans-only goal.
        else if (currentCans > multipackSize && currentCans < twoMultipackTotal && currentDry === 0) {
          const cansToGoal = twoMultipackTotal - currentCans;
          remainingText = `Add ${cansToGoal} more can${cansToGoal > 1 ? 's' : ''}`;
        }
        // Special case: if user has 1 dry bag, show options (add 1 dry to hit
        // 2 dry, OR add a full multipack of cans to hit the mixed goal).
        else if (currentDry === 1 && currentCans === 0) {
          remainingText = `Add 1 more dry bag OR add ${multipackSize} cans`;
        }
        // Default case: show what's needed for the closest goal
        else {
          remainingText = 'Add ';
          const parts = [];
          if (cansNeeded > 0) parts.push(`${cansNeeded} more can${cansNeeded > 1 ? 's' : ''}`);
          if (dryNeeded > 0) parts.push(`${dryNeeded} more dry bag${dryNeeded > 1 ? 's' : ''}`);
          remainingText += parts.join(' and ');
        }

        this.updateGoalDisplay(`Goal: ${closestGoal.name}`, remainingText);
      }
    }

    this.isGoalMet = false;
  }

  updateGoalDisplay(goalText, remainingText) {
    const goalTextEl = document.querySelector('.trail-box-goal-text');
    const remainingTextEl = document.querySelector('.trail-box-remaining-text');
    const progressMessageEl = document.querySelector('.trail-box-progress-message');
    
    if (goalTextEl) goalTextEl.textContent = goalText;
    if (remainingTextEl) remainingTextEl.textContent = remainingText;
    
    // Add/remove completed class based on the remaining text content
    if (progressMessageEl) {
      // Check if the trail box is complete by looking for the completion emoji
      if (remainingText && remainingText.includes('🎉')) {
        progressMessageEl.classList.add('completed');
      } else {
        progressMessageEl.classList.remove('completed');
      }
    }
  }

  getTotalQuantityByType(type) {
    // Get current cat's selections
    const catKey = this.currentCat || 'cat1';
    const catSelections = this.selections[catKey] || [];
    return catSelections
      .filter(s => s.type === type)
      .reduce((total, s) => total + s.quantity, 0);
  }

  updateInputStates() {
    // Disable inputs that would violate goal rules or inventory limits
    const productCards = this.querySelectorAll('.product-card');
    productCards.forEach(card => {
      const productId = card.dataset.productId;
      const productType = card.dataset.productType;
      const decreaseBtn = card.querySelector('.quantity-decrease');
      const increaseBtn = card.querySelector('.quantity-increase');
      const quantityInput = card.querySelector('.quantity-input');
      const currentQuantity = parseInt(quantityInput.value) || 0;
      const productImage = card.querySelector('.product-image');
      const inventoryStatus = card.querySelector('.product-inventory-status');
      
      // Get available inventory considering all cats
      const availableInventory = this.getAvailableInventory(productId, card);
      const totalInventory = parseInt(card.dataset.inventoryQuantity || '0');
      const inventoryPolicy = card.dataset.inventoryPolicy || 'deny';
      
      // Check if completely out of stock (either no inventory or all used by other cats)
      if (totalInventory <= 0 && inventoryPolicy !== 'continue') {
        // Out of stock - disable all controls
        if (decreaseBtn) decreaseBtn.disabled = true;
        if (increaseBtn) increaseBtn.disabled = true;
        if (quantityInput) {
          quantityInput.disabled = true;
          quantityInput.value = 0;
        }
        if (productImage) productImage.style.opacity = '0.5';
        if (inventoryStatus) {
          inventoryStatus.style.display = 'block';
          inventoryStatus.textContent = 'Out of stock';
        }
      } else if (availableInventory === 0 && currentQuantity === 0) {
        // All inventory used by other cats
        if (decreaseBtn) decreaseBtn.disabled = true;
        if (increaseBtn) increaseBtn.disabled = true;
        if (quantityInput) quantityInput.disabled = false; // Keep enabled but can't increase
        if (productImage) productImage.style.opacity = '0.7';
        if (inventoryStatus) {
          inventoryStatus.style.display = 'block';
          inventoryStatus.textContent = 'Used by other cats';
        }
      } else {
        // In stock - normal behavior
        if (productImage) productImage.style.opacity = '1';
        if (inventoryStatus) inventoryStatus.style.display = 'none';
        if (quantityInput) quantityInput.disabled = false;
        
        // Disable decrease if at 0
        if (decreaseBtn) {
          decreaseBtn.disabled = currentQuantity === 0;
        }
        
        if (increaseBtn) {
          // Check if we can add more considering multi-cat inventory
          const canAddMore = availableInventory > 0;
          
          // Check if adding one more of this type would be valid
          const canAddByGoal = this.canAddQuantity(productType, 1);
          increaseBtn.disabled = !canAddByGoal || !canAddMore;
        }
      }
    });
  }

  closeTrailBox() {
    // Check if goal is met before allowing close
    if (!this.isGoalMet) {
      const catKey = this.currentCat || 'cat1';
      const catSelections = this.selections[catKey] || [];
      const totalItems = catSelections.reduce((total, s) => total + s.quantity, 0);
      
      // Show error toast with specific message about valid goals. Numbers
      // derive from the live multi-pack size (7+1, 14 today; 6+1, 12 after
      // the supplier swap).
      if (window.QuizUtils && window.QuizUtils.Error) {
        if (totalItems === 0) {
          window.QuizUtils.Error.show('Please select at least one product for your trial box');
        } else if (totalItems < this.multipackSize) {
          window.QuizUtils.Error.show(
            `You have ${totalItems} item${totalItems === 1 ? '' : 's'} selected. ` +
            `Please add more products to meet minimum requirements (${this.multipackSize} cans, or 2 dry bags minimum)`
          );
        } else {
          window.QuizUtils.Error.show(`Please complete your trial box selection. Valid goals: ${this.twoMultipackTotal} cans OR ${this.multipackSize} cans + 1 dry bag OR 2 dry bags`);
        }
      }
      return; // Prevent closing
    }
    
    // If validation passes, proceed with closing
    const trailBoxSystem = document.querySelector('.trail-box-system');
    if (trailBoxSystem) {
      trailBoxSystem.classList.remove('trail-box-system-active');

      // Hide overlay
      const overlay = document.querySelector('.quiz-modal-overlay');
      if (overlay) overlay.classList.remove('active');

      // Re-enable body scrolling
      document.body.classList.remove('overflow-hidden');

      // Update item counts (handles both single-cat and multi-cat)
      if (window.fuzzballQuiz && window.fuzzballQuiz.updateTrailBoxCountsAfterClose) {
        setTimeout(() => {
          window.fuzzballQuiz.updateTrailBoxCountsAfterClose();
        }, 100);
      }
    }
  }


  autoPopulateFromQuiz(catSpecificRecommendations = null) {
    let submissionData, recommendationsData;

    // Set currentCat based on editingCat if available
    if (this.editingCat) {
      this.currentCat = `cat${this.editingCat}`;
    }

    // Preserve user edits — if the user has manually changed quantities in
    // the trail-box editor for this cat, do NOT clobber them on subsequent
    // step-7 entries. Restore their UI from saved selections and bail. The
    // un-touched case (default) falls through to the clear+re-populate path
    // below so quiz answer changes flow through to the trail-box composition.
    const catKey = this.currentCat || `cat${this.editingCat || 1}`;
    const isUserEdited = !!(window.QuizState
      && window.QuizState.get
      && window.QuizState.get('selections.trailUserEdited')?.[catKey]);
    if (isUserEdited) {
      this.restoreUIFromSelections();
      return;
    }

    if (catSpecificRecommendations) {
      // Use provided cat-specific data
      recommendationsData = catSpecificRecommendations;
      
      // Extract submission data from recommendations
      submissionData = {
        fuzzballer_name: recommendationsData.catName,
        breed: recommendationsData.breed,
        age_range: recommendationsData.ageRange,
        wet_dry_preference: recommendationsData.diet,
        lifestyle: recommendationsData.lifestyle,
        disliked_flavors: recommendationsData.dislikes || []
      };
    } else {
      // Get quiz recommendations from localStorage
      const quizData = localStorage.getItem('submissionData');
      const recommendations = localStorage.getItem('quizRecommendations');
      
      if (!quizData) return;
      
      const parsedData = JSON.parse(quizData);
      recommendationsData = recommendations ? JSON.parse(recommendations) : null;
      
      // Check if this is multi-cat data structure
      if (parsedData.cat1) {
        // Multi-cat structure - get the first cat by default
        submissionData = parsedData.cat1;
        
        // If we have multi-cat recommendations, use the first cat's
        if (recommendationsData && recommendationsData.cats && recommendationsData.cats.cat1) {
          recommendationsData = recommendationsData.cats.cat1;
        }
      } else {
        // Old single-cat structure
        submissionData = parsedData;
      }
    }
    
    console.log('%c=== 🎁 TRAIL BOX AUTO-POPULATE DEBUG START ===', 'background: #4CAF50; color: white; padding: 5px; font-weight: bold;');
    
    // Log all user answers
    
    // Clear current selections
    this.clearAllSelections();
    
    // Get dislikes
    const dislikes = submissionData.disliked_flavors || [];
    
    // Log all products before filtering
    const allCans = Array.from(this.querySelectorAll('.product-card[data-product-type="can"]'));
    const allDry = Array.from(this.querySelectorAll('.product-card[data-product-type="dry"]'));
    
    console.log('All Cans:', allCans.map(card => ({
      title: card.querySelector('.product-title')?.textContent,
      inventory: card.dataset.inventoryQuantity,
      policy: card.dataset.inventoryPolicy
    })));
    console.log('All Dry:', allDry.map(card => ({
      title: card.querySelector('.product-title')?.textContent,
      inventory: card.dataset.inventoryQuantity,
      policy: card.dataset.inventoryPolicy
    })));
    
    // Determine if the cat is a kitten
    const ageRange = submissionData.age_range || '';
    const isKitten = ageRange === 'Kitten' || ageRange.toLowerCase().includes('kitten') || ageRange.toLowerCase().includes('under 1');
    
    // Get lifestyle for filtering
    const lifestyle = submissionData.lifestyle || recommendationsData?.lifestyle;
    
    // Get food preference for filtering
    const foodPreference = submissionData.wet_dry_preference || 'Both';
    
    // Filter available products
    const cansResult = this.getFilteredProductsWithFallback('can', dislikes, isKitten, lifestyle, foodPreference);
    const availableCans = cansResult.cards;
    const availableDry = this.getFilteredProducts('dry', dislikes, isKitten, lifestyle, foodPreference);
    
    console.log('Available Cans:', availableCans.map(card => ({
      title: card.querySelector('.product-title')?.textContent,
      inventory: card.dataset.inventoryQuantity
    })));
    console.log('Available Dry:', availableDry.map(card => ({
      title: card.querySelector('.product-title')?.textContent,
      inventory: card.dataset.inventoryQuantity
    })));
    
    console.log('\nFiltering Summary:', { 
      totalCansBeforeFilter: allCans.length,
      totalDryBeforeFilter: allDry.length,
      cansAfterFilter: availableCans.length, 
      dryAfterFilter: availableDry.length 
    });
    
    // Determine best goal based on inventory AND user preference
    // Already have foodPreference from above
    // Convert to match Trail Box expectations
    const dietPreference = foodPreference === 'Wet' ? 'Wet food only' : 
                          foodPreference === 'Dry' ? 'Dry food only' : 
                          'Mix of both';
    const bestGoal = this.determineBestGoalOption(availableCans, availableDry, dietPreference);
    
    // Auto-select products based on inventory-aware goal
    if (bestGoal.cans > 0) {
      this.autoSelectProducts(availableCans, 'can', bestGoal.cans);
    }
    if (bestGoal.dry > 0) {
      this.autoSelectProducts(availableDry, 'dry', bestGoal.dry);
    }
    if (cansResult.usedDeprioritised && bestGoal.cans > 0) {
      this.showDeprioritisedFallbackToast();
    }
    
    // Log final selections (catKey already declared above)
    const catSelections = this.selections[catKey] || [];
    catSelections.forEach(selection => {
    });
    
    // Add Trail Box Summary
    console.log('1. Dislike Filtering:', {
      totalProductsBeforeFiltering: allCans.length + allDry.length,
      productsFilteredByDislikes: (allCans.length + allDry.length) - (availableCans.length + availableDry.length),
      productsAfterDislikeFilter: availableCans.length + availableDry.length
    });
    console.log('2. Inventory Impact:', {
      limitedInventoryProducts: availableCans.filter(c => parseInt(c.dataset.inventoryQuantity) < 999).length + 
                                availableDry.filter(c => parseInt(c.dataset.inventoryQuantity) < 999).length
    });
    console.log('3. Goal Achievement:', {
      targetGoal: bestGoal,
      goalMet: catSelections.reduce((total, s) => total + s.quantity, 0) === (bestGoal.cans + bestGoal.dry),
      alternativeGoalUsed: bestGoal.option === 'custom'
    });
    
    // Show message if using non-standard goal due to inventory
    if (bestGoal.option === 'custom') {
      const totalSelected = catSelections.reduce((total, s) => total + s.quantity, 0);
      // Less than minimum valid goal — threshold scales with the live
      // multi-pack size (smallest cans-based valid goal is multipackSize + 1 dry).
      const isInsufficientForGoal = totalSelected < this.multipackSize;
      
      if (window.QuizUtils && window.QuizUtils.Error) {
        if (isInsufficientForGoal) {
          // Critical: Not enough products for any valid goal
          window.QuizUtils.Error.show(
            `Only ${totalSelected} item${totalSelected === 1 ? '' : 's'} available based on your preferences. ` +
            'Please click "Edit Trail Box" below to add more products or update your preferences.',
            5000 // Show for 5 seconds
          );
          
          // Auto-open the trail box modal after a short delay
          setTimeout(() => {
            console.log('%c⚠️ AUTO-OPENING TRAIL BOX', 'background: #FF5722; color: white; padding: 3px; font-weight: bold;');
            this.openTrailBox();
          }, 1500); // Wait 1.5 seconds so user can read the message
        } else {
          // Just adjusted but still valid
          window.QuizUtils.Error.show('Trail box adjusted based on available inventory');
        }
      }
    }
    
    // Update UI
    this.updateSummary();
    this.updateGoalProgress();
    this.updateInputStates();
    
    // If we have very limited selection, update the goal display immediately.
    // NOTE: `totalSelected` referenced here is the block-scoped const declared
    // inside the `bestGoal.option === 'custom'` branch above (pre-existing
    // shape — out of scope to refactor here). The 7 literal becomes the live
    // multi-pack size so the threshold scales with the customizer setting.
    if (bestGoal.option === 'custom' && totalSelected < this.multipackSize) {
      // Override the normal goal display with a helpful message
      const goalTextEl = document.querySelector('.trail-box-goal-text');
      const remainingTextEl = document.querySelector('.trail-box-remaining-text');
      
      if (goalTextEl) goalTextEl.textContent = 'Limited selection available';
      if (remainingTextEl) {
        if (totalSelected === 1) {
          remainingTextEl.textContent = 'Please add more products to complete your trail box';
        } else {
          remainingTextEl.textContent = `Only ${totalSelected} items available - please add more`;
        }
      }
    }
    
    console.log('%c=== 🎁 TRAIL BOX DEBUG END ===\n', 'background: #4CAF50; color: white; padding: 5px; font-weight: bold;');
    
    // Save selections after auto-populating
    this.saveSelections();
    
    // Update single cat trail box count if needed
    if (window.fuzzballQuiz && window.fuzzballQuiz.updateSingleCatTrailBoxCount) {
      window.fuzzballQuiz.updateSingleCatTrailBoxCount();
    }
  }

  // Flavour states (Theme settings → Quiz — Flavours & Trial Box, Sep 2026):
  // a Deprioritised flavour renders in the grid so the customer can add it,
  // but auto-pick and top-ups only reach for it as a last resort. Strict pass
  // first (Available flavours only); if the cat's dislikes leave nothing, fall
  // back to the Deprioritised flavours rather than ship an empty or dry-only
  // box. Hidden flavours never render, so they can never be picked.
  getFilteredProductsWithFallback(type, dislikes, isKitten = null, lifestyle = null, foodPreference = null) {
    const strict = this.getFilteredProducts(type, dislikes, isKitten, lifestyle, foodPreference, false);
    if (strict.length > 0 || type !== 'can') {
      return { cards: strict, usedDeprioritised: false };
    }
    const relaxed = this.getFilteredProducts(type, dislikes, isKitten, lifestyle, foodPreference, true);
    if (relaxed.length > 0) {
      console.log('📦 No Available flavours left after dislikes; falling back to Deprioritised flavours');
    }
    return { cards: relaxed, usedDeprioritised: relaxed.length > 0 };
  }

  showDeprioritisedFallbackToast() {
    if (window.QuizUtils && window.QuizUtils.Error) {
      window.QuizUtils.Error.show('Some flavours are limited right now, so we\'ve built your box from our other flavours');
    }
  }

  getFilteredProducts(type, dislikes, isKitten = null, lifestyle = null, foodPreference = null, allowDeprioritised = false) {
    const selector = `.product-card[data-product-type="${type}"]`;
    const productCards = Array.from(this.querySelectorAll(selector));

    return productCards.filter(card => {
      const title = card.querySelector('.product-title')?.textContent || '';
      const inventoryQty = parseInt(card.dataset.inventoryQuantity || '0');
      const inventoryPolicy = card.dataset.inventoryPolicy || 'deny';

      if (!allowDeprioritised && card.dataset.flavourState === 'deprioritised') return false;

      // Check quiz filters (dislikes)
      if (!this.passesQuizFilters(title, dislikes)) return false;
      
      // Check age-based filtering for kitten food
      if (isKitten !== null && !this.passesAgeFilter(title, isKitten, type, foodPreference)) return false;
      
      // Check lifestyle-based filtering (similar to bundle system)
      if (lifestyle && !this.passesLifestyleFilter(title, lifestyle, type, isKitten, foodPreference)) return false;
      
      // Check inventory availability
      if (inventoryPolicy !== 'continue' && inventoryQty <= 0) {
        return false;
      }
      
      return true;
    });
  }

  passesQuizFilters(productTitle, dislikes) {
    const title = productTitle.toLowerCase();
    
    // Check dislikes - case insensitive matching
    if (dislikes && dislikes.length > 0) {
      for (const dislike of dislikes) {
        if (title.includes(dislike.toLowerCase())) {
          return false;
        }
      }
    }
    
    return true;
  }
  
  passesAgeFilter(productTitle, isKitten, productType = null, foodPreference = null) {
    const title = productTitle.toLowerCase();
    
    // Check if this is a kitten-specific product
    if (title.includes('kitten food')) {
      if (!isKitten) {
        return false;
      } else {
      }
    } else if (isKitten && !title.includes('kitten')) {
      // For kittens, allow adult wet food if wet food is preferred OR mixed diet
      if (productType === 'can' && (foodPreference === 'Wet' || foodPreference === 'Both')) {
        return true;
      }
      // Otherwise, exclude adult products for kittens
      return false;
    }
    
    return true;
  }
  
  passesLifestyleFilter(productTitle, lifestyle, productType, isKitten, foodPreference = null) {
    // Lifestyle filtering ONLY applies to dry food for adult cats
    // Wet food (cans) should NOT have lifestyle restrictions
    if (productType !== 'dry' || isKitten) {
      return true;
    }

    const title = productTitle.toLowerCase();

    if (lifestyle === 'Indoor' || lifestyle === 'Both') {
      // Indoor/Both cats: only chicken, tuna, and salmon for DRY food (indoor yellow bag)
      const hasApprovedFlavor = title.includes('chicken') || title.includes('tuna') || title.includes('salmon');
      if (!hasApprovedFlavor) {
        return false;
      }
    } else if (lifestyle === 'Outdoor') {
      // Outdoor cats: only turkey for DRY food
      if (!title.includes('turkey')) {
        return false;
      }
    }

    return true;
  }
  
  currentCatHasDislikes() {
    // Check if the current cat has any dislikes
    const quizData = localStorage.getItem('submissionData');
    if (!quizData) return false;
    
    const parsedData = JSON.parse(quizData);
    
    // Handle multi-cat vs single-cat data
    let catData;
    if (parsedData.cat1) {
      // Multi-cat data
      catData = parsedData[`cat${this.currentCat}`] || parsedData.cat1;
    } else {
      // Single cat data
      catData = parsedData;
    }
    
    const dislikes = catData?.disliked_flavors || [];
    
    return dislikes.length > 0;
  }

  autoSelectProducts(productCards, type, targetQuantity) {
    if (productCards.length === 0) {
      return;
    }
    
    // Even distribution across the eligible flavours (profit ranking
    // removed Sep 2026; flavour states in theme settings decide the pool).
    const sortedCards = [...productCards];

    // Create inventory tracker with multi-cat awareness
    const inventoryTracker = {};
    sortedCards.forEach(card => {
      const productId = card.dataset.productId;
      const productTitle = card.querySelector('.product-title')?.textContent;
      // Use getAvailableInventory to consider other cats' usage
      const availableForThisCat = this.getAvailableInventory(productId, card);
      inventoryTracker[productId] = {
        title: productTitle,
        available: availableForThisCat,
        used: 0,
        card: card
      };
    });
    
    // Calculate distribution plan: even split, remainder to the first cards
    const qtyPerProduct = Math.floor(targetQuantity / sortedCards.length);
    const remainder = targetQuantity % sortedCards.length;
    const distributionPlan = sortedCards.map((card, index) => ({
      product: card,
      quantity: qtyPerProduct + (index < remainder ? 1 : 0)
    }));

    let distributed = 0;

    // Distribute according to plan
    for (const { product: card, quantity: plannedQty } of distributionPlan) {
      const productId = card.dataset.productId;
      const quantityInput = card.querySelector('.quantity-input');
      const inventory = inventoryTracker[productId];
      
      // Calculate how many we can actually add (respect inventory)
      const availableInventory = inventory.available - inventory.used;
      const toAdd = Math.min(plannedQty, availableInventory);
      
      if (toAdd > 0 && quantityInput) {
        const currentQty = parseInt(quantityInput.value) || 0;
        const newQty = currentQty + toAdd;
        
        quantityInput.value = newQty;
        this.updateSelection(productId, type, newQty);
        inventory.used += toAdd;
        distributed += toAdd;
      } else if (availableInventory <= 0) {
      }
    }
    
    // If we couldn't distribute everything due to inventory, try to fill from remaining products
    if (distributed < targetQuantity) {
      
      for (const card of sortedCards) {
        if (distributed >= targetQuantity) break;
        
        const productId = card.dataset.productId;
        const quantityInput = card.querySelector('.quantity-input');
        const inventory = inventoryTracker[productId];
        const availableInventory = inventory.available - inventory.used;
        
        if (availableInventory > 0 && quantityInput) {
          const remainingTarget = targetQuantity - distributed;
          const toAdd = Math.min(remainingTarget, availableInventory);
          
          const currentQty = parseInt(quantityInput.value) || 0;
          const newQty = currentQty + toAdd;
          
          quantityInput.value = newQty;
          this.updateSelection(productId, type, newQty);
          inventory.used += toAdd;
          distributed += toAdd;
          
        }
      }
    }
    
    if (distributed < targetQuantity) {
    } else {
    }
  }

  calculateAvailableInventory(productCards) {
    let totalAvailable = 0;
    productCards.forEach(card => {
      const inventoryQty = parseInt(card.dataset.inventoryQuantity || '0');
      const inventoryPolicy = card.dataset.inventoryPolicy || 'deny';
      totalAvailable += inventoryPolicy === 'continue' ? 999 : inventoryQty;
    });
    return Math.min(totalAvailable, 999); // Cap at reasonable number
  }

  determineBestGoalOption(availableCans, availableDry, foodPreference = 'Mix of both') {
    const canInventory = this.calculateAvailableInventory(availableCans);
    const dryInventory = this.calculateAvailableInventory(availableDry);

    // Derive thresholds from the live multi-pack size so the inventory-aware
    // goal selection scales with the customizer setting (multipackSize → 7 today,
    // 6 after the supplier swap; twoMultipackTotal → 14 today, 12 after).
    const multipackSize = this.multipackSize;
    const twoMultipackTotal = this.twoMultipackTotal;

    // Determine goal based on user preference AND inventory
    if (foodPreference === 'Wet food only') {
      // User wants wet only - prioritize cans-only goal (2 × multipack)
      if (canInventory >= twoMultipackTotal) {
        return { cans: twoMultipackTotal, dry: 0, option: 'option1' };
      } else if (canInventory >= multipackSize && dryInventory >= 1) {
        // Fall back to mixed if not enough cans
        return { cans: multipackSize, dry: 1, option: 'option2' };
      } else if (dryInventory >= 2) {
        return { cans: 0, dry: 2, option: 'option3' };
      }
    } else if (foodPreference === 'Dry food only') {
      // User wants dry only - prioritize 2 dry bags
      if (dryInventory >= 2) {
        return { cans: 0, dry: 2, option: 'option3' };
      } else if (canInventory >= multipackSize && dryInventory >= 1) {
        // Fall back to mixed if not enough dry
        return { cans: multipackSize, dry: 1, option: 'option2' };
      } else if (canInventory >= twoMultipackTotal) {
        return { cans: twoMultipackTotal, dry: 0, option: 'option1' };
      }
    } else {
      // User wants "Mix of both" - PRIORITIZE multipack cans + 1 dry
      if (canInventory >= multipackSize && dryInventory >= 1) {
        return { cans: multipackSize, dry: 1, option: 'option2' };
      } else if (canInventory >= twoMultipackTotal) {
        // Fall back to wet only if can't do mixed
        return { cans: twoMultipackTotal, dry: 0, option: 'option1' };
      } else if (dryInventory >= 2) {
        // Fall back to dry only
        return { cans: 0, dry: 2, option: 'option3' };
      }
    }

    // If none of the standard options work, use custom with what's available
    if (canInventory >= multipackSize) {
      return { cans: multipackSize, dry: 0, option: 'custom' };
    } else if (dryInventory >= 1) {
      return { cans: 0, dry: 1, option: 'custom' };
    } else {
      return { cans: Math.min(canInventory, twoMultipackTotal), dry: 0, option: 'custom' };
    }
  }

  clearAllSelections() {
    // Clear current cat's selections only
    const catKey = this.currentCat || 'cat1';
    this.selections[catKey] = [];
    
    // Only clear UI inputs if we're in single-cat mode or viewing the current cat
    if (!this.editingCat || this.editingCat === catKey.replace('cat', '')) {
      const quantityInputs = this.querySelectorAll('.quantity-input');
      quantityInputs.forEach(input => {
        input.value = 0;
      });
    }
    
    // Save cleared state
    this.saveSelections();
  }

  // Public methods for external access
  openTrailBox(catSpecificRecommendations = null) {
    const trailBoxSystem = document.querySelector('.trail-box-system');
    if (trailBoxSystem) {
      trailBoxSystem.classList.add('trail-box-system-active');
      
      // Show overlay
      const overlay = document.querySelector('.quiz-modal-overlay');
      if (overlay) overlay.classList.add('active');
      
      // Get the cat number being edited
      const editingCat = trailBoxSystem.getAttribute('data-editing-cat');
      if (editingCat) {
        this.currentCat = `cat${editingCat}`;
      }
      
      // Check if we're in edit mode before auto-populating
      const editMode = window.QuizState.get('editMode');
      console.log('📦 openTrailBox - Edit mode state:', editMode);
      
      if (editMode?.active) {
        // In edit mode, restore products from cart
        if (editMode.products?.trail) {
          this.restoreEditModeProducts(editMode);
        } else {
          console.log('📦 No trail products to restore in edit mode');
        }
        
        // Set up return to cart button if in restricted edit mode
        if (editMode.restricted) {
          console.log('📦 Trail box open: Setting up return to cart button (restricted edit mode)');
          // Set up the button after a small delay to ensure DOM is ready
          setTimeout(() => this.setupReturnToCartButton(), 100);
        }
      } else {
        // Check if we have existing selections for this cat
        const catKey = this.currentCat || 'cat1';
        const catSelections = this.selections[catKey] || [];
        
        if (catSelections.length > 0) {
          // User has already made selections - preserve them
          this.restoreUIFromSelections();
        } else {
          // No existing selections - auto-populate from recommendations
          if (catSpecificRecommendations) {
            // Use cat-specific recommendations
            this.autoPopulateFromQuiz(catSpecificRecommendations);
          } else {
            // Use global recommendations
            this.autoPopulateFromQuiz();
          }
        }
      }
      
      // Recalculate global inventory usage when opening
      this.calculateGlobalInventoryUsage();
      
      // Always update input states to check inventory
      this.updateInputStates();
      
      // Update the summary to ensure counts are accurate
      this.updateSummary();
      
      // Also update the main quiz controller's trail box count
      if ((this.currentCat === 'cat1' || !this.currentCat) && window.fuzzballQuiz && window.fuzzballQuiz.updateSingleCatTrailBoxCount) {
        setTimeout(() => {
          window.fuzzballQuiz.updateSingleCatTrailBoxCount();
        }, 100);
      }
    }
  }

  getSelections(returnAllCats = false) {
    // If explicitly requested to return all cats, or if in multi-cat mode
    const isMultiCat = window.QuizState.get('submissionData.catCount') > 1;
    const actualCatCount = window.QuizState.get('submissionData.catCount') || 1;
    
    if (returnAllCats || isMultiCat) {
      // Return all selections combined with cat number info preserved
      let allSelections = [];
      Object.entries(this.selections).forEach(([catKey, catSelections]) => {
        // Extract cat number from key (e.g., 'cat2' -> 2)
        const catNumber = parseInt(catKey.replace('cat', ''));
        
        // IMPORTANT: Only include selections for cats that actually exist
        if (catNumber <= actualCatCount && Array.isArray(catSelections) && catSelections.length > 0) {
          // Already have cat_number from updateSelection
          allSelections = allSelections.concat(catSelections);
        }
      });
      return allSelections;
    }
    
    // Single cat mode or specific cat requested
    const catKey = this.currentCat || 'cat1';
    return this.selections[catKey] || [];
  }

  // Save current selections to localStorage
  saveSelections() {
    const step10Data = JSON.parse(localStorage.getItem('step10Selections') || '{}');
    step10Data.trailBox = this.selections;
    step10Data.timestamp = Date.now();
    localStorage.setItem('step10Selections', JSON.stringify(step10Data));
  }
  
  // Load selections from localStorage
  loadSelections() {
    const step10Data = JSON.parse(localStorage.getItem('step10Selections') || '{}');
    if (step10Data.trailBox) {
      this.selections = step10Data.trailBox;
      return true;
    }
    return false;
  }

  // Trial-box flavour toggles (Aug 2026): remove saved selections whose
  // flavour is no longer rendered (switched off in Theme settings →
  // Quiz — Trail Box) and top each affected cat's cans back up to its goal
  // from the remaining flavours. Data-only (no DOM writes) so it can run for
  // every cat before any UI restore; restoreUIFromSelections and the summary
  // components render the result.
  reconcileSelectionsWithPool() {
    const allowedCans = this.trailBoxProducts?.cans || [];
    const allowedIds = new Set(
      [...allowedCans, ...(this.trailBoxProducts?.dry || [])].map(p => String(p.id))
    );
    let changed = false;

    Object.keys(this.selections || {}).forEach(catKey => {
      const catSelections = this.selections[catKey];
      if (!Array.isArray(catSelections) || catSelections.length === 0) return;

      const kept = catSelections.filter(sel => allowedIds.has(String(sel.id)));
      if (kept.length === catSelections.length) return;

      console.log(`📦 ${catKey}: removed ${catSelections.length - kept.length} selection(s) for flavours no longer offered`);
      this.selections[catKey] = kept;
      changed = true;

      let cans = 0;
      let dry = 0;
      kept.forEach(sel => {
        if (sel.type === 'can') cans += sel.quantity;
        else if (sel.type === 'dry') dry += sel.quantity;
      });
      const canTarget = dry >= 2 ? 0 : (dry === 1 ? this.multipackSize : this.twoMultipackTotal);
      const deficit = canTarget - cans;
      if (deficit <= 0) return;

      // Same pool rules as auto-pick: every Available flavour minus this
      // cat's dislikes, falling back to Deprioritised flavours if that
      // leaves nothing.
      const dislikes = this.getCatDislikes(catKey);
      let pool = allowedCans.filter(product =>
        !product.deprioritised && this.passesQuizFilters(product.title || '', dislikes)
      );
      if (pool.length === 0) {
        pool = allowedCans.filter(product => this.passesQuizFilters(product.title || '', dislikes));
      }
      if (pool.length === 0) return;

      const catNumber = catKey.replace('cat', '') || '1';
      const qtyPerProduct = Math.floor(deficit / pool.length);
      const remainder = deficit % pool.length;
      pool.forEach((product, index) => {
        const quantity = qtyPerProduct + (index < remainder ? 1 : 0);
        if (quantity <= 0) return;
        const existing = kept.find(sel => String(sel.id) === String(product.id));
        if (existing) {
          existing.quantity += quantity;
        } else {
          kept.push({
            id: product.id,
            variant_id: product.variant_id,
            title: product.title,
            type: 'can',
            quantity: quantity,
            cat_number: catNumber
          });
        }
      });
      console.log(`📦 ${catKey}: topped up ${deficit} can(s) from remaining flavours`);
    });

    if (changed) {
      this.saveSelections();
    }
  }

  // Dislikes for a specific cat, mirroring currentCatHasDislikes' data access.
  getCatDislikes(catKey) {
    const quizData = localStorage.getItem('submissionData');
    if (!quizData) return [];
    try {
      const parsedData = JSON.parse(quizData);
      const catNumber = String(catKey || 'cat1').replace('cat', '') || '1';
      const catData = parsedData.cat1 ? (parsedData[`cat${catNumber}`] || parsedData.cat1) : parsedData;
      return catData?.disliked_flavors || [];
    } catch (e) {
      return [];
    }
  }
  
  // Legacy method for compatibility
  saveSelectionsForEdit() {
    this.saveSelections();
  }
  
  // Restore UI from saved selections
  restoreUIFromSelections() {
    const catKey = this.currentCat || 'cat1';

    // Trial-box flavour toggles (Aug 2026): drop entries whose card no longer
    // renders instead of silently skipping them below while leaving them in
    // this.selections — that desyncs the goal counter from the visible box.
    const savedSelections = this.selections[catKey] || [];
    const catSelections = savedSelections.filter(selection =>
      this.querySelector(`[data-product-id="${selection.id}"]`)
    );
    if (catSelections.length !== savedSelections.length) {
      this.selections[catKey] = catSelections;
      this.saveSelections();
    }

    // Clear all inputs first
    const allQuantityInputs = this.querySelectorAll('.quantity-input');
    allQuantityInputs.forEach(input => {
      input.value = 0;
    });

    // Restore each saved selection
    catSelections.forEach(selection => {
      const productCard = this.querySelector(`[data-product-id="${selection.id}"]`);
      if (productCard) {
        const quantityInput = productCard.querySelector('.quantity-input');
        if (quantityInput) {
          quantityInput.value = selection.quantity;
        }
      }
    });
    
    // Update UI components
    this.updateSummary();
    this.updateGoalProgress();
    this.updateInputStates();
  }
  
  showQuantityAnimation(productCard) {
    if (!productCard || typeof lottie === 'undefined') return;
    
    const quantityControls = productCard.querySelector('.quantity-controls');
    if (!quantityControls) return;
    
    // Check if animation container already exists
    let animationContainer = quantityControls.querySelector('.quantity-lottie-overlay');
    
    if (!animationContainer) {
      // Create animation container
      animationContainer = document.createElement('div');
      animationContainer.className = 'quantity-lottie-overlay';
      quantityControls.appendChild(animationContainer);
    }
    
    // Clear any existing animation
    animationContainer.innerHTML = '';
    
    // Load and play Lottie animation
    const animation = lottie.loadAnimation({
      container: animationContainer,
      renderer: 'svg',
      loop: false,
      autoplay: false,
      path: window.quantitySelectorAnimationUrl || '/assets/quantity-selector-ani.json'
    });
    
    // Show animation
    animationContainer.classList.add('active');
    animation.play();
    
    // Hide animation after it completes
    animation.addEventListener('complete', () => {
      animationContainer.classList.remove('active');
      setTimeout(() => {
        animation.destroy();
      }, 200); // Wait for fade out transition
    });
  }
}

// Register custom element
customElements.define('trail-box', TrailBoxBuilder);